import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import logger from '@elab/core/utils/logger';
import analytics from '@elab/core/utils/analytics';
import { dialogConstants, externalReviewsVendorPopupName } from '@elab/core/utils/constants';
import { selectors as userSelectors } from '@elab/core/store/user';
import { actions as globalActions, types as globalSagaTypes } from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';
import { getURL } from '@elab/core/utils/address';
import { getLoginRedirectURL } from '@elab/core/utils/appNavigation';
import { selectors as productSelectors, types as productTypes } from '@elab/core/store/product';

import { getReportedReviews, saveReportedReview } from './helpers';
import api from './api';

import { actions, selectors, types } from '.';

const addReviewPopupName = 'review-popup';
const nonVerifiedPurchasePopup = 'review-error-popup';
const pendingStatus = 'pending';

export function* loadReviews({ preload, success, error }) {
    if (preload) yield call(preload);

    yield put(actions.setLoading(true));

    const params = yield all({
        pageSize: select(selectors.getNumberOfItemsPerPage),
        currentPage: select(selectors.getCurrentPage),
        activeFilters: select(selectors.getActiveFilters),
        productCode: select(selectors.getProductCode),
        sort: select(selectors.getActiveSort),
        isAvis: select(selectors.isAvis),
        isPaginated: select(selectors.isPaginated),
    });

    try {
        let { results, pagination } = yield call(api.loadReviews, params);
        pagination = paginationDTOGenerator(pagination);
        const { totalResults } = pagination;
        if (success) yield call(success, { results, totalResults, pagination });
    } catch (e) {
        yield call(logger.error, e);
        if (error) yield call(error);
    } finally {
        yield put(actions.setLoading(false));
    }
}

function paginationDTOGenerator(_pagination) {
    return {
        currentPage: _pagination.page || _pagination.currentPage || 0,
        totalResults: _pagination.totalCount || _pagination.totalResults,
        totalPages: _pagination.totalPages,
    };
}

export function* getPage({ payload }) {
    yield put(actions.setCurrentPage(payload.page));
    yield call(loadReviews, { success: setReviewsWith(actions.setReviews) });
}

export function* tryIncrementPage() {
    const allReviewsLoaded = yield select(selectors.getAllReviewsLoadedStatus);
    if (!allReviewsLoaded) {
        const currPage = yield select(selectors.getCurrentPage);
        yield put(actions.setCurrentPage(currPage + 1));
    }
}

export function* voteForReview({ payload: { id, vote } }) {
    try {
        yield call(api.voteForReview, id, vote);
        yield put(actions.loadReviews());
    } catch (error) {
        yield call(logger.error, error);
    }
}

export function* tryDecrementPage() {
    const currPage = yield select(selectors.getCurrentPage);
    if (currPage > 0) {
        yield put(actions.setCurrentPage(currPage - 1));
    }
}

export function* tryOpenExternalReviewsVendor() {
    const userId = yield select(userSelectors.getUserId);

    if (userId) {
        yield put(uiActions.openDialog({ name: externalReviewsVendorPopupName }));
        return;
    }

    const URLs = yield all({
        current: call(getURL),
        login: call(getLoginRedirectURL),
    });
    yield put(
        globalActions.setPrevPageData({
            url: URLs.current,
        }),
    );
    yield put(globalActions.redirect(URLs.login));
}

export function* reportReview({ payload }) {
    try {
        const reportedReviews = yield select(selectors.getReportedReviews);
        if (!reportedReviews.includes(payload)) {
            yield call(api.reportReview, payload);

            reportedReviews.push(payload);
            yield call(saveReportedReview, payload);
            yield put(actions.setReportedReviews(reportedReviews));
        }
    } catch (error) {
        yield call(logger.error, error);
    }
}

function* loadReportedReviews() {
    const reportedReviews = yield call(getReportedReviews);
    yield put(actions.setReportedReviews(reportedReviews));
}

// creates worker saga with customizable behavior
function reviewLoader(cfg) {
    return function*() {
        yield call(loadReviews, cfg);
    };
}

function setReviewsWith(actionCreator) {
    return function*({ results, totalCount, pagination }) {
        yield put(actionCreator({ results, totalCount, pagination }));
    };
}

function putOnce(action) {
    return function*() {
        yield put(action);
    };
}

export function* openAddReviewPopup(redirection = false) {
    try {
        const { productCode } = yield select(productSelectors.getProductCodeForAddProductReview);
        const verifiedStatus = yield call(api.getProductReviewVerifiedPurchaser, productCode);
        const booleanVerifiedStatus = verifiedStatus == 'true';
        if (booleanVerifiedStatus) {
            yield put(uiActions.openDialog({ name: addReviewPopupName }));
        } else {
            yield put(uiActions.openDialog({ name: nonVerifiedPurchasePopup }));
        }
        if (redirection && typeof redirection === 'boolean') {
            const target = document.querySelector('#product-reviews-summary');
            yield put(uiActions.scrollToTarget({ target }));
            yield take(productTypes.SET_PRODUCT);
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* openLoginPopup() {
    yield call(openPopupWithAuthCheck, true, 'login');
}

export function* setCurrentReview({ payload }) {
    yield put(uiActions.openDialog({ name: payload.dialogName }));
}

export function* openAddReviewPopupByUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const popupName = urlParams.get(dialogConstants.getParamName);
    if (popupName && popupName == pendingStatus) {
        const { productCode } = yield select(productSelectors.getProductCodeForAddProductReview);
        const verifiedStatus = yield call(api.getProductReviewVerifiedPurchaser, productCode);
        const booleanVerifiedStatus = verifiedStatus == 'true';
        if (booleanVerifiedStatus) {
            yield call(setPopupUrlParams, addReviewPopupName);
        } else {
            yield call(setPopupUrlParams, nonVerifiedPurchasePopup);
        }
        yield call(openPopupWithAuthCheck, true);
    } else {
        if (urlParams) {
            const popupName = urlParams.get(dialogConstants.getParamName);
            if (popupName && popupName == addReviewPopupName) {
                yield call(openPopupWithAuthCheck, true);
            }
        }
    }
}

export function* setPopupUrlParams(popupName) {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(dialogConstants.getParamName, popupName);
    const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
    yield put(
        globalActions.setPrevPageData({
            url: newUrl,
        }),
    );
}

export function* openPopupWithAuthCheck(redirection, caller) {
    const isAnonymous = yield select(userSelectors.isAnonymous);
    if (isAnonymous) {
        if (caller === 'login') {
            yield call(setPopupUrlParams, pendingStatus);
        } else {
            yield call(setPopupUrlParams, addReviewPopupName);
        }
        const redirectURL = yield call(getLoginRedirectURL);
        yield put(globalActions.redirect(redirectURL));
    } else {
        yield call(openAddReviewPopup, redirection);
    }
}

export default [
    takeLatest(types.OPEN_EXTERNAL_REVIEWS_VENDOR, tryOpenExternalReviewsVendor),
    takeLatest(
        types.LOAD_MORE_REVIEWS,
        reviewLoader({
            preload: putOnce(actions.tryIncrementPage()),
            success: setReviewsWith(actions.setMoreReviews),
            error: putOnce(actions.tryDecrementPage()),
        }),
    ),
    takeLatest(
        [
            types.SET_PRODUCT_CODE,
            types.LOAD_REVIEWS,
            types.REMOVE_ALL_FILTERS,
            types.ADD_ACTIVE_FILTER,
            types.SET_ACTIVE_FILTER,
            types.REMOVE_ACTIVE_FILTER,
            types.SET_SORT,
        ],
        reviewLoader({
            preload: putOnce(actions.setCurrentPage(0)),
            success: setReviewsWith(actions.setReviews),
        }),
    ),
    takeLatest(types.TRY_INCREMENT_PAGE, tryIncrementPage),
    takeLatest(types.TRY_DECREMENT_PAGE, tryDecrementPage),
    takeLatest(types.OPEN_LOGIN, openLoginPopup),
    takeLatest(types.OPEN_ADD_REVIEW_POPUP, openAddReviewPopup),
    takeEvery(globalSagaTypes.PAGE_LOAD, openAddReviewPopupByUrl),
    takeEvery(types.VOTE_FOR_REVIEW, voteForReview),
    takeEvery([types.ADD_ACTIVE_FILTER, types.SET_ACTIVE_FILTER], function*() {
        yield call(analytics.handlePDPReviewFilters);
    }),
    takeEvery(types.REPORT_REVIEW, reportReview),
    takeEvery(types.LOAD_REPORTED_REVIEWS, loadReportedReviews),
    takeEvery(types.GET_PAGE, getPage),
    takeEvery(types.SET_CURRENT_REVIEW, setCurrentReview),
];
